import { alpha, type Theme } from '@mui/material/styles';
import createTheme from '@mui/material/styles/createTheme';
import { debounce } from 'lodash';
import { StateCreator } from 'zustand';

import { ThemeState } from './types/ThemeState';
import { ThemeStoreDependencies } from './types/ThemeStoreDependencies';

export const themeStore =
  ({ useLeadChannelStore }: ThemeStoreDependencies): StateCreator<ThemeState> =>
  (set, get) => ({
    breakpoints: {
      lg1: 1336,
      md1: 872,
      md2: 920,
      sm1: 616,
      sm2: 744,
    },
    colors: {
      borderColor: '#c0c8d8',
      btnDisabledColor: '#f5f5f5',
      btnSecondaryColor: '#f5f5f5',
      checkboxBorderColor: '#cbcbcb',
      checkboxContentColor: '#666666',
      drawerBtnColor: '#000000',
      drawerLabelColor: '#999999',
      errorColor: '#d32f2f',
      inputDisabledColor: '#f5f5f5',
      modalBackgroundColor: '#ffffff',
      modalDividerColor: '#c0c8d8',
      primaryColor: '#0C4650',
      progressEmptyColor: '#f5f5f5',
      radioBorderColor: '#c0c8d8',
      stepperContentColor: '#666666',
      stepperLineColor: '#c0c8d8',
      successColor: '#5d9e51',
    },
    measuredPageHeight:
      document.documentElement.clientHeight || window.innerHeight,
    opacity: {
      selectedTint: 0.16,
    },

    computed: {
      get primaryColor(): string {
        const { leadChannel } = useLeadChannelStore.getState();

        return leadChannel.primaryColor || get().colors.primaryColor;
      },
    },

    createTheme: (): Theme => {
      const {
        computed: { primaryColor },
        breakpoints,
        colors,
        measuredPageHeight,
        opacity,
      } = get();

      return createTheme({
        components: {
          MuiAlert: {
            styleOverrides: {
              filledInfo: ({ theme }: { theme: Theme }) => ({
                '& .MuiAlert-action': {
                  alignSelf: 'flex-end',
                  margin: 0,
                  padding: 0,
                  position: 'absolute',
                  right: '1rem',
                  top: '1.5rem',
                },
                '& .MuiAlert-message': {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 0,
                  width: '100%',
                },
                '& .MuiAlertTitle-root': {
                  color: 'white',
                  fontWeight: 700,
                  marginBottom: '0.5rem',
                },
                '& .MuiIconButton-root': {
                  color: 'white',
                  stroke: 'white',
                  strokeWidth: '0.6',
                },
                backgroundColor: '#3d3e4d',
                color: '#bcbdc2',
                display: 'flex',
                flexDirection: 'column',
                margin: '0 0.5rem',
                position: 'relative',
                // padding must come after paddingRight to avoid overwriting it.
                // eslint-disable-next-line sort-keys
                paddingRight: '3rem',
                // eslint-disable-next-line sort-keys
                padding: '1.5rem 1rem',

                [theme.breakpoints.up(breakpoints.sm1)]: {
                  maxWidth: '21rem',
                },
              }),
              filledSuccess: {
                backgroundColor: '#323232',
                color: 'white',
              },
              root: {
                '& .MuiAlertTitle-root': {
                  fontWeight: 700,
                },
                '& .MuiIconButton-root': {
                  fontWeight: 700,
                  paddingTop: '0',
                },
                fontWeight: 500,
                padding: '1rem 1.5rem',
                width: '100%',
              },
              standardSuccess: {
                '& .MuiAlert-icon': {
                  color: 'white',
                },
                backgroundColor: colors.successColor,
                color: 'white',
              },
            },
          },
          MuiButton: {
            defaultProps: {
              disableRipple: true,
            },
            styleOverrides: {
              containedPrimary: {
                '&&:hover': {
                  backgroundColor: primaryColor,
                  boxShadow: 'unset',
                  opacity: '0.8',
                },
                '&:disabled': {
                  backgroundColor: colors.btnDisabledColor,
                },
                color: 'white',
              },
              containedSecondary: {
                '&&:hover': {
                  backgroundColor: colors.btnSecondaryColor,
                  boxShadow: 'unset',
                  opacity: '0.8',
                },
                backgroundColor: colors.btnSecondaryColor,
                color: 'black',
              },
              outlinedPrimary: {
                '&&:hover': {
                  backgroundColor: colors.btnSecondaryColor,
                  borderColor: colors.borderColor,
                  boxShadow: 'unset',
                  opacity: '0.8',
                },
                borderColor: colors.borderColor,
                color: 'black',
              },
              root: {
                borderRadius: '4px',
                boxShadow: 'unset',
                fontSize: '1rem',
                fontWeight: 500,
                minHeight: '3.5rem',
                textTransform: 'none',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                padding: '8px',
              },
            },
          },
          MuiCheckbox: {
            defaultProps: {
              disableRipple: true,
            },
            styleOverrides: {
              root: {
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem',
                },
                color: colors.checkboxBorderColor,
                padding: '0',
              },
            },
          },
          /**
           * use MuiCssBaseline for specific theme assignments to custom classes.
           */
          MuiCssBaseline: {
            styleOverrides: {
              '.checkbox-described': {
                '& .MuiFormControlLabel-root': {
                  margin: '0',
                },
                '& .MuiFormControlLabel-root .MuiTypography-root': {
                  color: 'black',
                  fontSize: '1rem',
                  fontWeight: 500,
                  lineHeight: '1.5rem',
                  marginLeft: '1.5rem',
                },
                '& p': {
                  color: colors.checkboxContentColor,
                  fontSize: '1rem',
                  fontWeight: 500,
                  lineHeight: '1.5rem',
                  margin: '1.5rem 0 0 0',
                },
                border: `2px solid ${colors.checkboxBorderColor}`,
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '1.5rem',
              },
              '.checkbox-described-checked': {
                border: `2px solid ${primaryColor}`,
              },
              '.checkbox-described-error': {
                border: `2px solid ${colors.errorColor}`,
              },
              '.MuiAlert-standard.generic': {
                backgroundColor: 'black',
                color: 'white',
              },
              '.navigation-pane': {
                '& .MuiButtonBase-root.Mui-selected': {
                  backgroundColor: alpha(primaryColor, opacity.selectedTint),
                  color: primaryColor,
                },
                '& .MuiButtonBase-root.Mui-selected:hover': {
                  backgroundColor: alpha(primaryColor, opacity.selectedTint),
                },
                '& .MuiListItemIcon-root': {
                  color: 'inherit',
                  minWidth: '2.5rem',
                },
                '& .MuiListItemText-primary': {
                  color: colors.drawerLabelColor,
                  fontWeight: 500,
                  marginBottom: '1rem',
                  padding: '0 1.5rem',
                },
                '& .MuiListItemText-secondary': {
                  color: 'inherit',
                  fontSize: '1rem',
                  fontWeight: 600,
                  lineHeight: '1.5rem',
                },
                a: {
                  borderRadius: '8px',
                  color: colors.drawerLabelColor,
                  height: '4.5rem',
                  marginBottom: '11px',
                  padding: '1.5rem',
                },
              },
              '.radio-group': {
                '& .radio-selected': {
                  borderColor: primaryColor,
                  borderWidth: '2px',
                },
                '& input:checked + span:first-of-type': {
                  '&::before': {
                    // inner radio circle
                    backgroundColor: primaryColor,
                  },
                  borderColor: primaryColor,
                  borderWidth: '2px',
                },
                '& label': {
                  borderColor: colors.radioBorderColor,
                  borderWidth: '1px',
                },
                '& label:hover': {
                  borderColor: primaryColor,
                },
              },
            },
          },
          MuiDrawer: {
            defaultProps: {
              PaperProps: {
                style: {
                  padding: '52px 0',
                  width: '17rem',
                },
              },
            },
            styleOverrides: {
              root: {
                '& .navigation-pane a': {
                  borderRadius: '0',
                },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontWeight: 500,
              },
            },
          },
          MuiLinearProgress: {
            styleOverrides: {
              bar: {
                backgroundColor: primaryColor,
              },
              colorPrimary: {
                backgroundColor: colors.progressEmptyColor,
              },
            },
          },
          MuiModal: {
            styleOverrides: {
              root: {
                '& .MuiBox-root': {
                  background: colors.modalBackgroundColor,
                  height: '100%',
                  outline: 'none',
                  overflowY: 'auto',
                  padding: '0 1.5rem',
                  width: 'min(100%, 568px)',
                },
                '& .MuiStepContent-last': {
                  borderLeft: '1px solid transparent',
                },
                '& .MuiStepper-root': {
                  padding: '0',
                },
                '& .MuiStepper-root .MuiCollapse-wrapperInner': {
                  WebkitLineClamp: 'unset',
                },
                '& footer': {
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'right',
                  padding: '1.5rem 0 3rem 0',
                },
                '& header': {
                  background: 'white',
                  borderBottom: `1px solid ${colors.modalDividerColor}`,
                  display: 'flex',
                  h1: {
                    color: 'black',
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    margin: '0',
                  },
                  justifyContent: 'space-between',
                  padding: '3rem 0',
                  position: 'sticky',
                  top: '0',
                },
                '& header button': {
                  alignItems: 'center',
                  color: 'black',
                  cursor: 'pointer',
                  display: 'flex',
                  padding: '0',
                },
                '& main': {
                  paddingTop: '3rem',
                },
                '& main p': {
                  margin: '0',
                  paddingBottom: '1.5rem',
                },
                '@media (min-width: 576px)': {
                  '& .MuiBox-root': {
                    borderRadius: '0.5rem',
                    height: 'unset',
                    minHeight: '435px',
                  },
                  '& footer button': {
                    maxWidth: '248px',
                  },
                  '& main': {
                    maxHeight: '508px',
                    minHeight: '174px',
                    overflowY: 'auto',
                  },
                },
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                '&:hover:not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: primaryColor,
                  },
                // MuiOutlinedInput can be an input or select element - query allows us to target both.
                '*[class*="Mui-disabled"]': {
                  backgroundColor: colors.inputDisabledColor,
                },
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: 500,
              },
            },
          },
          MuiPopover: {
            styleOverrides: {
              root: {
                '& .MuiMenuItem-root': {
                  whiteSpace: 'normal',
                },
              },
            },
          },
          MuiRadio: {
            styleOverrides: {
              root: {
                color: colors.radioColor,
              },
            },
          },
          MuiSelect: {
            defaultProps: {
              MenuProps: {
                // Ensures the menu opens below the select field
                anchorOrigin: {
                  horizontal: 'left',
                  vertical: 'bottom',
                },
                // Prevent body scroll locking to avoid address bar repositioning on mobile browsers
                disableScrollLock: true,
                slotProps: {
                  paper: {
                    style: {
                      // On certain smaller devices where the screen height is limited, this will prevent
                      // the menu from taking up the entire screen making the first option of the select inaccessible.
                      maxHeight: 'min(max(240px, 30vw), 400px)',
                      minHeight: '120px',
                      // Prevent scroll chaining inside of the menu (hitting the end of menu wont fling the page)
                      overscrollBehavior: 'contain',
                      // Use instant (non-animated) scrolling to the selected option within menu to prevent scroll jumping issues on mobile devices
                      scrollBehavior: 'auto',
                      // Trigger hardware acceleration to improve mobile scrolling performance (especially older devices)
                      transform: 'translateZ(0)',
                    },
                  },
                },
                // Point of which the popover will attach to the anchor's origin
                transformOrigin: {
                  horizontal: 'left',
                  vertical: 'top',
                },
              },
            },
            styleOverrides: {
              icon: {
                color: 'black',
              },
            },
          },
          MuiStepper: {
            styleOverrides: {
              root: {
                '& .Mui-active [class*="MuiStepConnector-line"]': {
                  borderLeft: `1px solid ${primaryColor}`,
                },
                '& .Mui-completed .MuiStepContent-root': {
                  borderLeft: `1px solid ${primaryColor}`,
                },
                '& .Mui-completed [class*="MuiStepConnector-line"]': {
                  borderLeft: `1px solid ${primaryColor}`,
                },
                '& .MuiCollapse-wrapperInner': {
                  display: '-webkit-box',
                  marginBottom: '2rem',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: '3',
                },
                '& .MuiStep-root:not(:first-of-type)': {
                  paddingTop: '0.5rem',
                },
                '& .MuiStepConnector-line': {
                  borderLeft: `1px solid ${colors.stepperLineColor}`,
                },
                // last step being presented in the stepper
                '& .MuiStepContent-last': {
                  paddingBottom: '0.5rem',
                },
                '& .MuiStepContent-root': {
                  borderLeft: `1px solid ${colors.stepperLineColor}`,
                  color: colors.stepperContentColor,
                  fontSize: '1rem',
                  fontWeight: '500',
                  paddingLeft: '2.25rem',
                },
                // last progress step determined via progressStore `isLastStep` method
                '& .MuiStepContent-root.lastProgressStep': {
                  borderLeft: '1px solid transparent',
                },
                '& .MuiStepLabel-iconContainer': {
                  ':not([class*="Mui-active"]):not([class*="Mui-completed"])': {
                    color: colors.stepperLineColor,
                  },
                  color: primaryColor,
                  paddingRight: '0',
                },
                '& .MuiStepLabel-label': {
                  color: colors.stepperContentColor,
                  fontSize: '1rem',
                  fontWeight: '700',
                  paddingLeft: '1.5rem',
                },
                '& .MuiStepLabel-label.Mui-active': {
                  color: 'black',
                  fontSize: '1rem',
                  fontWeight: '700',
                },
                '& .MuiStepLabel-label.Mui-completed': {
                  color: 'black',
                  fontSize: '1rem',
                  fontWeight: '700',
                },
                '& .MuiStepLabel-root': {
                  paddingTop: '0',
                },
                padding: '1.5rem 1.5rem 0 1.5rem',
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: '0.75rem',
                fontWeight: 300,
                lineHeight: '1.2rem',
              },
            },
          },
        },
        pageLayout: {
          display: 'flex',
          flexDirection: 'column',
          height: measuredPageHeight ? `${measuredPageHeight}px` : 'auto',
        },
        palette: {
          primary: {
            main: primaryColor,
          },
        },
        typography: {
          fontFamily: '"Montserrat", Verdana, sans-serif',
        },
      });
    },

    initializePageHeightMeasurement: (): void => {
      const { updatePageHeight } = get();

      // Initial page height measurement
      updatePageHeight();

      // Listen on browser resize and orientation change events
      window.addEventListener('resize', updatePageHeight);
      window.addEventListener('orientationchange', updatePageHeight);

      // Observe DOM mutations (ex: inserted/removed nodes that change layout)
      const mutationObserver = new MutationObserver(updatePageHeight);
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Observe element resize (ex: content changes that affect height)
      const resizeObserver = new ResizeObserver(updatePageHeight);
      resizeObserver.observe(document.body);
      resizeObserver.observe(document.documentElement);
    },
    updatePageHeight: debounce(() => {
      const { measuredPageHeight } = get();

      const LANDSCAPE_RIGHT_DEGREE = 90;
      const LANDSCAPE_LEFT_DEGREE = -90;

      const isLandscape =
        // Safari versions older than 16.4 do not support the orientation property
        // @link https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation#browser_compatibility
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        screen.orientation?.type.startsWith('landscape') ||
        window.orientation === LANDSCAPE_RIGHT_DEGREE || // support for older browsers
        window.orientation === LANDSCAPE_LEFT_DEGREE;

      const contentOverflows = document.body.scrollHeight > window.innerHeight;

      let rawHeight =
        window.visualViewport?.height || // Prefer visualViewport when available as it accounts for browser zoom scaling
        document.documentElement.clientHeight ||
        window.innerHeight;

      // Capture any zoom styling applied to the document via CSS
      const zoomStyling = parseFloat(
        window
          .getComputedStyle(document.documentElement)
          .getPropertyValue('zoom') || '1',
      );

      // If document zoom styling is applied, compensate for it
      if (zoomStyling !== 1) {
        rawHeight = rawHeight / zoomStyling;
      }

      const newHeight =
        isLandscape && contentOverflows
          ? null // If landscape and content overflows, we don't set a fixed height (defaults to auto)
          : Math.floor(rawHeight); // Round down to avoid potential 1px overflow

      if (measuredPageHeight !== newHeight) {
        set({ measuredPageHeight: newHeight });
      }
    }, 100),
  });
